// @catalog F.030
// ProductDetailDrawer — Orchestrator del rediseño 2026-05-21.
// Reemplaza ProductDetailDialog.tsx (modal centrado MUI) por un drawer
// lateral derecho de 820px controlado manualmente (Box position fixed +
// backdrop) — el AGENT_PROMPT del handoff exige NO usar <Dialog> MUI.
//
// Reutiliza al 100% la lógica de fetch + PATCH del dialog viejo (vive en
// useProductDetailDrawerData). El layout y los 5 tabs se componen sobre
// archivos hermanos para respetar P7 (<=500 líneas en cada uno).

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { InventoryDetailsDialog } from "../../InventoryDetailsDialog";
import { ProductDetailHeader } from "./ProductDetailHeader";
import { DecisionCard } from "./DecisionCard";
import { TabResumen } from "./TabResumen";
import { TabPorTienda } from "./TabPorTienda";
import { TabHistorico } from "./TabHistorico";
import { TabCatalogo } from "./TabCatalogo";
import { TabSistema } from "./TabSistema";
import { DrawerTabs, type TabKey } from "./DrawerTabs";
import { useProductDetailDrawerData } from "./useProductDetailDrawerData";
import { fmtNum } from "./helpers";

export interface ProductDetailDrawerProps {
  open: boolean;
  unifiedProductId: string | null;
  productName?: string | undefined;
  onClose: () => void;
  // Persiste el flag activeForPurchase (compras ON/OFF) en backend + lista.
  onToggleActiveForPurchase?: ((unifiedProductId: string) => void) | undefined;
}

export function ProductDetailDrawer({
  open,
  unifiedProductId,
  productName,
  onClose,
  onToggleActiveForPurchase,
}: ProductDetailDrawerProps): React.ReactElement | null {
  const {
    data,
    loading,
    error,
    competitors,
    competitorsLoading,
    productImage,
    storeSales,
    storeSalesLoading,
    storeSalesError,
    saving,
    saved,
    savePurchaseCant,
  } = useProductDetailDrawerData(open, unifiedProductId);

  // UI state local
  const [tab, setTab] = useState<TabKey>("resumen");
  const [purchaseQty, setPurchaseQty] = useState("");
  const [inventoryOpen, setInventoryOpen] = useState(false);
  // Toggle Compras ON/OFF. Flip optimista local + persistencia vía
  // onToggleActiveForPurchase (PATCH /forecasting/product-mapping/:id/
  // active-for-purchase + actualización de la lista padre).
  const [activeForPurchase, setActiveForPurchase] = useState(true);

  // Reset UI state cuando cambia el producto
  useEffect(() => {
    if (open && unifiedProductId) setTab("resumen");
  }, [open, unifiedProductId]);

  // Hydrata input + toggle desde el mapping cuando llega data
  useEffect(() => {
    if (data?.mapping) {
      const cur = data.mapping.purchaseCantUser;
      setPurchaseQty(cur != null ? String(cur) : "");
      setActiveForPurchase(data.mapping.activeForPurchase);
    }
  }, [data?.mapping]);

  // Lock body scroll mientras el drawer está abierto
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // ESC para cerrar
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Aplica accent teal en el documento cuando el drawer está abierto.
  // Restaura el accent previo al cerrar (otros paneles pueden requerir
  // su propio accent).
  const previousAccentRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) return;
    const ds = document.documentElement.dataset;
    previousAccentRef.current = ds["accent"] ?? null;
    ds["accent"] = "teal";
    return () => {
      if (previousAccentRef.current == null) {
        delete ds["accent"];
      } else {
        ds["accent"] = previousAccentRef.current;
      }
    };
  }, [open]);

  const handleApply = useCallback(() => {
    const raw = purchaseQty.trim();
    if (raw === "") {
      void savePurchaseCant(null);
      return;
    }
    let val = parseInt(raw, 10);
    if (Number.isNaN(val)) {
      void savePurchaseCant(null);
      return;
    }
    // Pack size mínimo = 1 (nunca cero). El UI ya valida con min={1}
    // y los steppers no bajan de 1, pero validamos defensivamente aquí también.
    if (val < 1) val = 1;
    if (val > 10000) val = 10000;
    void savePurchaseCant(val);
  }, [purchaseQty, savePurchaseCant]);

  const handleResetPurchaseCant = useCallback(() => {
    void savePurchaseCant(null);
  }, [savePurchaseCant]);

  const tabCounts = useMemo(
    () => ({
      tiendas: storeSales?.stores.length ?? null,
      historico: data?.monthlySales.length ?? null,
      catalogo: competitors?.competitors.length ?? null,
    }),
    [storeSales, data, competitors],
  );

  if (!open) return null;

  return (
    <>
      <Box
        onClick={onClose}
        aria-hidden
        sx={{
          position: "fixed",
          inset: 0,
          bgcolor: "rgba(13, 18, 32, 0.32)",
          // z-index 1199 deja que cualquier MUI Dialog (default 1300) abierto
          // desde dentro del drawer (ej. InventoryDetailsDialog) quede ENCIMA.
          zIndex: 1199,
          animation: "fdrawer-backdrop-fade 0.2s ease",
          "@keyframes fdrawer-backdrop-fade": {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
        }}
      />
      <Box
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-detail-drawer-title"
        sx={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(820px, 92vw)",
          bgcolor: "var(--bg)",
          zIndex: 1200,
          boxShadow: "-24px 0 60px rgba(13, 18, 32, 0.18)",
          display: "flex",
          flexDirection: "column",
          animation: "fdrawer-slide 0.28s cubic-bezier(0.32, 0.72, 0.24, 1)",
          "@keyframes fdrawer-slide": {
            from: { transform: "translateX(100%)" },
            to: { transform: "translateX(0)" },
          },
        }}
      >
        {loading ? (
          <Box sx={{ flex: 1, display: "grid", placeItems: "center", p: 4 }}>
            <Stack spacing={1.5} alignItems="center">
              <CircularProgress size={32} />
              <Typography sx={{ fontSize: 13, color: "var(--ink-3)" }}>
                Cargando detalle...
              </Typography>
            </Stack>
          </Box>
        ) : error ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : !data || !data.mapping ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="info">Sin datos disponibles.</Alert>
          </Box>
        ) : (
          <>
            <ProductDetailHeader
              mapping={data.mapping}
              productImage={productImage}
              activeForPurchase={activeForPurchase}
              onToggleActive={() => {
                setActiveForPurchase((prev) => !prev);
                if (unifiedProductId)
                  onToggleActiveForPurchase?.(unifiedProductId);
              }}
              onClose={onClose}
            />
            {/* Banner inventario — dato crítico, sticky bajo el header. Click
                abre InventoryDetailsDialog (sub-modal con desglose por tienda). */}
            <Box
              role="button"
              tabIndex={0}
              onClick={() => setInventoryOpen(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setInventoryOpen(true);
                }
              }}
              sx={{
                mx: 2.5,
                mt: 1.5,
                mb: 0.5,
                bgcolor: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: 1.25,
                px: 1.75,
                py: 1.25,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                transition: "background 0.15s",
                "&:hover": {
                  bgcolor: "var(--bg-2)",
                  borderColor: "var(--accent)",
                },
                "&:focus-visible": {
                  outline: "2px solid var(--accent)",
                  outlineOffset: 2,
                },
              }}
            >
              <Inventory2OutlinedIcon
                sx={{ fontSize: 20, color: "var(--accent)" }}
              />
              <Stack
                direction="row"
                spacing={1}
                alignItems="baseline"
                sx={{ flex: 1, minWidth: 0 }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "var(--ink-3)",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    fontWeight: 500,
                  }}
                >
                  Inventario actual
                </Typography>
                <Typography
                  sx={{
                    fontSize: 18,
                    fontWeight: 600,
                    color: "var(--ink-1)",
                    fontVariantNumeric: "tabular-nums",
                    letterSpacing: "-0.015em",
                  }}
                >
                  {fmtNum(data.totalInventory)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "var(--ink-3)",
                  }}
                >
                  pzas
                </Typography>
              </Stack>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "var(--ink-3)",
                  fontWeight: 500,
                }}
              >
                Ver desglose por tienda
              </Typography>
              <ChevronRightIcon sx={{ color: "var(--ink-3)", fontSize: 18 }} />
            </Box>
            <DecisionCard
              mapping={data.mapping}
              totalInventory={data.totalInventory}
              purchaseQty={purchaseQty}
              setPurchaseQty={setPurchaseQty}
              saving={saving}
              saved={saved}
              onApply={handleApply}
              onReset={handleResetPurchaseCant}
            />
            <DrawerTabs
              value={tab}
              onChange={setTab}
              tiendasCount={tabCounts.tiendas}
              historicoCount={tabCounts.historico}
              catalogoCount={tabCounts.catalogo}
            />
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                px: 2.5,
                pt: 2,
                pb: 6,
                bgcolor: "var(--bg)",
              }}
            >
              <Typography
                id="product-detail-drawer-title"
                component="h2"
                sx={{
                  position: "absolute",
                  width: 1,
                  height: 1,
                  overflow: "hidden",
                  clip: "rect(0 0 0 0)",
                  whiteSpace: "nowrap",
                }}
              >
                Detalle del producto {productName ?? data.mapping.unifiedName}
              </Typography>
              {tab === "resumen" && <TabResumen data={data} />}
              {tab === "tiendas" && (
                <TabPorTienda
                  storeSales={storeSales}
                  loading={storeSalesLoading}
                  errorMessage={storeSalesError?.message ?? null}
                />
              )}
              {tab === "historico" && (
                <TabHistorico sales={data.monthlySales} />
              )}
              {tab === "catalogo" && (
                <TabCatalogo
                  competitors={competitors}
                  loading={competitorsLoading}
                />
              )}
              {tab === "sistema" && <TabSistema mapping={data.mapping} />}
            </Box>
          </>
        )}
      </Box>

      <InventoryDetailsDialog
        open={inventoryOpen}
        unifiedProductId={unifiedProductId}
        {...(productName !== undefined ? { productName } : {})}
        onClose={() => setInventoryOpen(false)}
      />

      <Snackbar
        open={saved}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message="Cantidad de compra guardada"
      />
    </>
  );
}
